import React, { useCallback, useRef } from "react";
import Select from "react-select";
import { HInput } from "../../../validation/HForm";
import {
  paymentOptions,
  paymentGroups,
  paymentLabels,
  paymentErrors,
} from "../../../helpers/data-constants";
import BasicAlert from "../Alert/Alert";
import usePaymentType from "./usePaymentType";

import "./paymentSelector.scss";

const customSelectStyles = {
  option: (provided, state) => ({
    ...provided,
    paddingLeft:
      state.data.label === paymentLabels[paymentGroups.CASH] ||
      state.data.label === paymentLabels[paymentGroups.BANK_TRANSACTION]
        ? "10px"
        : "25px",
  }),
};

const PaymentTypeSelector = ({
  form,
  maxTotalSum,
  calculateTotalSum,
  inputFieldName,
  isFilter = false,
  setCashChangePayment,
  handleSumChange,
  setPaymentInputs
}) => {
  const contentRef = useRef(null);

  const isPartialQuenching = handleSumChange;
  const isDisabled = !isPartialQuenching && !Number(maxTotalSum);
  const {
    paymentMethods,
    amountFields,
    handleTypeDeal,
    handleInputChange,
    calculateSum,
    handleInputBlur,
    calculateBalance,
    allInputs,
  } = usePaymentType(form, maxTotalSum, inputFieldName, setCashChangePayment, setPaymentInputs);

  const getMessage = () => {
    const balance = calculateBalance(calculateSum(paymentGroups.CASH));
    return balance < 0 ? paymentErrors.CASH_ERROR : "";
  };

  const message = getMessage();
  const isOpen = !!message;

  const handleInputUpdate = (itemValue, event) => {
    if (isPartialQuenching) {
      handleSumChange(event);
    } else {
      handleInputChange(itemValue, event.target.value);
    }
  };
  
  const filterOptions = (option) => {
    if (!isFilter) return true;
    if (option.options) {
      return option.options.some(({ isSaleGoods }) => !isSaleGoods);
    }
    return !option.data.isSaleGoods;
  };

  const getSummaryDetails = (balance, cashAmount, labels) => {
    let label = labels.total;
    let amount = calculateTotalSum.toFixed(2);

    if (balance < 0) {
      label = labels.total;
      amount = calculateTotalSum.toFixed(2);
    } else if (cashAmount === 0) {
      label = labels.noCash;
      amount = balance;
    } else if (cashAmount > balance) {
      label = labels.cashChange;
      amount = (cashAmount - balance).toFixed(2);
    }

    return { label, amount };
  };

  const renderSummaryBlock = useCallback(() => {
    const labels = {
      noCash: "Остаток по наличным:",
      total: "Итого внесено:",
      cashChange: "Сдача:",
    };

    const balance = calculateBalance(calculateSum(paymentGroups.CASH)).toFixed(2);
    const cashAmount = amountFields.cash;

    const { label, amount } = getSummaryDetails(balance, cashAmount, labels);

    return (
      <div className="nonCash-operationBloc">
        <h3 className="m-none">{label}</h3>
        <h3 className={labels.total ? "error m-none" : "m-none"}>{amount}</h3>
      </div>
    );
  }, [amountFields, calculateTotalSum]);

  const renderInputFields = useCallback(() => (allInputs.map((item) => (
      <div className="nonCash-operationItem" key={item.value}>
        <span className="nonCash-operationItemTitle">{item.label}</span>
        <div className="nonCash-input">
          <HInput h="true"
            type="number"
            className={`input ${item.value === paymentGroups.CASH && item.value}`}
            name={`${inputFieldName}.${item.value}`}
            step={0.01}
            onChange={(e) => handleInputUpdate(item.value, e)}
            onBlur={() => handleInputBlur(item.value)}
            disabled={isDisabled}
          />
        </div>
      </div>
    ))
  ), [paymentMethods, allInputs]);

  return (
    <>
      <div
        className={`nonCash-operationBloc payment-select ${
          isPartialQuenching ? "partial" : ""
        }`}
        style={{
          height: `${contentRef.current && contentRef.current.scrollHeight}px`,
          transition: "height 0.3s ease",
        }}
      >
        <div className="nonCash-operationItem">
          <Select
            classNamePrefix="select"
            className="overdue-searchItem"
            placeholder="Выберите тип оплаты"
            options={paymentOptions}
            value={allInputs}
            onChange={handleTypeDeal}
            hideSelectedOptions={false}
            controlShouldRenderValue={false}
            menuPosition="fixed"
            styles={{ ...customSelectStyles }}
            isDisabled={isDisabled}
            filterOption={filterOptions}
            isMulti
          />
        </div>
        <div className="d-flex flex-column">
          <div ref={contentRef}>
            <div className="nonCash-operationItem columns-2 payment-operationItem">
              <div className="nonCash-operation">{renderInputFields()}</div>
            </div>
            {allInputs.length && !isPartialQuenching
              ? renderSummaryBlock()
              : null}
          </div>
        </div>
      </div>
      <BasicAlert
        onClose={() => getMessage()}
        message={message}
        isOpen={isOpen}
        isError={true}
      />
    </>
  );
};

export default PaymentTypeSelector;
